import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { savePlanGroup } from 'redux/actionCreators';
import { v4 as uuidv4 } from 'uuid';

import { sortAlphabeticallyBy } from 'utils/misc';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';

import { ConfirmationDialog, Dialog } from 'components/shared';
import PlanUnitMultiSelect from 'components/shared/PlanUnitMultiSelect';

import PlanGroup, { PlanGroupType } from './PlanGroup';
import PlanGroupsDialogStyles from './PlanGroupsDialogStyles';
import { PlanGroup as IPlanGroup, PlanUnit } from 'components/types';
import { Icon } from '@sdb/planning-ui';

interface PlanGroupDialogProps {
  open?: boolean;
  onHidden: () => void;
}

export default function PlanGroupsDialog({
  open = true,
  onHidden
}: PlanGroupDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const planGroups = useSelector((state) => state.planGroups);
  const { hasKidsPlan } = useSelector((state) => state.grants);
  const [hide, setHide] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [selectedPlanGroupId, setSelectedPlanGroupId] = useState<
    string | number | undefined
  >();
  const [newPlanGroup, setNewPlanGroup] = useState<PlanGroupType | null>(null);
  const selectedPlanGroup: PlanGroupType | undefined =
    newPlanGroup || planGroups.find(({ id }) => id === selectedPlanGroupId);

  useEffect(() => {
    dispatch({ type: 'FETCH_PLANGROUPS' });
  }, [dispatch]);

  useEffect(() => {
    setNewPlanGroup(null);
  }, [planGroups]);

  const addNewPlanGroup = () => {
    const planGroup = {
      name: '',
      id: uuidv4(),
      costCenters: [],
      isNew: true
    };
    setNewPlanGroup(planGroup);
  };

  const onSelectedPlanGroup = ({ id }) => {
    setSelectedPlanGroupId(id);
    setNewPlanGroup(null);
  };

  const onSelectedPlanUnit = (planUnit) => {
    if (selectedPlanGroup) {
      const { isNew, costCenters } = selectedPlanGroup;
      const planGroup = { ...selectedPlanGroup };
      const index = selectedPlanGroup.costCenters.findIndex(
        ({ id }) => id === planUnit.id
      );
      if (index > -1) {
        planGroup.costCenters = [
          ...costCenters.slice(0, index),
          ...costCenters.slice(index + 1)
        ];
      } else {
        planGroup.costCenters = [...costCenters, planUnit];
      }
      if (isNew) {
        setNewPlanGroup(planGroup);
      } else {
        dispatch(savePlanGroup(planGroup));
      }
    }
  };

  const removePlanUnit = (planUnitID) => {
    if (selectedPlanGroup) {
      const { isNew, costCenters } = selectedPlanGroup;
      const planGroup = {
        ...selectedPlanGroup,
        costCenters: costCenters.filter(({ id }) => id !== planUnitID)
      };
      if (isNew) {
        setNewPlanGroup(planGroup);
      } else {
        dispatch(savePlanGroup(planGroup));
      }
    }
  };

  const handleClose = () => {
    if (newPlanGroup) {
      setShowConfirmationDialog(true);
    } else {
      setHide(true);
    }
  };

  const handleConfirmationAction = (isConfirmed) => {
    setShowConfirmationDialog(false);
    if (isConfirmed) {
      setHide(true);
    }
  };

  return (
    <Dialog
      title={t('managePlanGroups')}
      open={hide ? false : open}
      aria-labelledby="plan-groups-dialog"
      maxWidth="extralarge"
      onHidden={onHidden}
      handleClose={handleClose}
    >
      <PlanGroupsDialogStyles>
        <List component={Paper}>
          <div>
            <ListItem>
              <Button
                color="primary"
                variant="contained"
                disabled={!!newPlanGroup}
                onClick={addNewPlanGroup}
                className="add-btn"
              >
                {t('addPlanGroup')}
              </Button>
            </ListItem>
          </div>
          <div className="list-content">
            {newPlanGroup && (
              <PlanGroup
                key={newPlanGroup.id}
                planGroup={newPlanGroup}
                isSelected
              />
            )}
            {sortAlphabeticallyBy(
              planGroups.filter((pg: IPlanGroup) => pg && pg.name),
              'name'
            ).map((planGroup: IPlanGroup, index: number) => (
              <PlanGroup
                key={index}
                planGroup={planGroup}
                isSelected={Boolean(
                  selectedPlanGroup && selectedPlanGroup.id === planGroup.id
                )}
                onSelected={onSelectedPlanGroup}
              />
            ))}
          </div>
        </List>

        <List component={Paper}>
          <ListItem dense>
            <PlanUnitMultiSelect
              label={
                selectedPlanGroup ? t('selectPlanUnit') : t('selectPlanGroup')
              }
              selectedOptions={
                selectedPlanGroup ? selectedPlanGroup.costCenters || [] : []
              }
              onSelected={onSelectedPlanUnit}
              showGroups={false}
              disabled={!selectedPlanGroup}
            />
          </ListItem>
          <div className="list-content">
            {selectedPlanGroup &&
              sortAlphabeticallyBy(
                selectedPlanGroup.costCenters.filter(
                  (pg: PlanUnit) => pg && pg.name
                ),
                'name'
              ).map(({ name, id, isKids }) => (
                <div className="d-flex" key={id}>
                  <ListItem dense>
                    {hasKidsPlan && isKids && (
                      <Icon
                        type="children"
                        fontSize="small"
                        className="has-icon"
                      />
                    )}
                    <ListItemText primary={name} />
                  </ListItem>
                  <IconButton
                    color="primary"
                    onClick={() => removePlanUnit(id)}
                    size="large"
                  >
                    <Icon type="xmark" />
                  </IconButton>
                </div>
              ))}
          </div>
        </List>
      </PlanGroupsDialogStyles>
      {showConfirmationDialog && (
        <ConfirmationDialog
          handleAction={handleConfirmationAction}
          cancelBtnText={t('no')}
          confirmBtnText={t('yes')}
          title={t('confirmationDialogTitle')}
          type="warning"
        >
          <p>{t('unsavedChanges')}</p>
        </ConfirmationDialog>
      )}
    </Dialog>
  );
}
